import { GetProductCodeByWebIdQuery } from 'packages/shared/definitions/types';

import { dispatchTrackingEvent } from '@context/tracking/utils';

import { TrackedProduct } from './hooks/useTracking/model';

type LoadProductCodes = (
  ids: string[]
) => Promise<GetProductCodeByWebIdQuery['vpProductByIds']>;

/**
 * Revieve's hair payload carries no price, so price/full_price are sent as 0.
 * Confirm with analytics whether they need the real price enriched here.
 */
const getTrackingProduct = ({
  product,
  listName,
  productCode,
}: {
  product: TrackedProduct;
  productCode?: string | null;
  listName?: 'hair_anl_pdp' | 'hair_anl_list';
}) => ({
  brand_name: product.item_brand ?? '',
  full_price: 0,
  kind: '',
  price: 0,
  primary_subcategory: '',
  primary_category: product.item_category ?? '',
  primary_type: '',
  product_code: productCode ?? '',
  product_name: product.item_name ?? '',
  ...(listName && { list_name: listName }),
});

export const trackViewItem = async (
  product: TrackedProduct,
  loadProductCodes: LoadProductCodes
) => {
  if (!product.item_id) {
    return;
  }

  const prodCode = await loadProductCodes([product.item_id]);

  const trackingProduct = getTrackingProduct({
    product,
    productCode: prodCode[0]?.productCode,
    listName: 'hair_anl_pdp',
  });

  dispatchTrackingEvent({
    event: 'view_item',
    products: [trackingProduct],
    location: 'hair_anl',
    _clear: true,
  });
};

export const trackViewItemList = async (
  products: TrackedProduct[],
  loadProductCodes: LoadProductCodes
) => {
  const identifiableProducts = products.filter((product) => !!product.item_id);

  if (!identifiableProducts.length) {
    return;
  }

  const prodCode = await loadProductCodes(
    identifiableProducts.map((p) => p.item_id as string)
  );

  const trackingProducts = identifiableProducts.map((product) => ({
    ...getTrackingProduct({
      product,
      productCode: prodCode.find((code) => code?.id === product.item_id)
        ?.productCode,
      listName: 'hair_anl_list',
    }),
    list_position: 0,
  }));

  dispatchTrackingEvent({
    event: 'view_item_list',
    product_lists: {
      list_name: 'hair_anl_list',
      interaction: 'click',
      sorting_type: 'automatic',
      products: trackingProducts,
    },
    _clear: true,
  });
};

export const trackAddToCard = async (
  product: TrackedProduct,
  loadProductCodes: LoadProductCodes
) => {
  if (!product.item_id) {
    return;
  }

  const prodCode = await loadProductCodes([product.item_id]);

  const trackingProduct = getTrackingProduct({
    product,
    productCode: prodCode[0]?.productCode,
  });

  dispatchTrackingEvent({
    event: 'add_to_cart',
    product: {
      ...trackingProduct,
      quantity: 1,
      cart_type: 'hair_anl',
    },
    _clear: true,
  });
};

export const trackAddToWishList = async (
  product: TrackedProduct,
  timing: number,
  loadProductCodes: LoadProductCodes
) => {
  if (!product.item_id) {
    return;
  }

  const prodCode = await loadProductCodes([product.item_id]);

  const trackingProduct = getTrackingProduct({
    product,
    productCode: prodCode[0]?.productCode,
  });

  dispatchTrackingEvent({
    event: 'add_to_wishlist',
    wishlist: {
      action: 'product_added_to_wishlist',
      interaction: 'click_add',
      type: 'hair_anl',
      timing,
      products: [trackingProduct],
    },
    _clear: true,
  });
};

export const trackRemoveFromWishList = async (
  product: TrackedProduct,
  timing: number,
  loadProductCodes: LoadProductCodes
) => {
  if (!product.item_id) {
    return;
  }

  const prodCode = await loadProductCodes([product.item_id]);

  const trackingProduct = getTrackingProduct({
    product,
    productCode: prodCode[0]?.productCode,
  });

  dispatchTrackingEvent({
    event: 'remove_from_wishlist',
    wishlist: {
      action: 'product_removed_from_wishlist',
      interaction: 'click_add',
      type: 'hair_anl',
      timing,
      products: [trackingProduct],
    },
    _clear: true,
  });
};

export const trackViewOrSelectPromotion = (
  brand: string,
  event: 'select_promotion' | 'view_promotion'
) => {
  dispatchTrackingEvent({
    event,
    promotions: [
      {
        code: 'hair_anl',
        id: brand,
        location: 'hair_anl_pdp',
        creative: '',
        destination_url: '',
        name: '',
        position: 0,
      },
    ],
    _clear: true,
  });
};
